import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Form, Input, Button, message } from 'antd';
import { ArrowLeft } from 'lucide-react';
import useApi from '@src/api_service/useApi';
import { debounce } from 'lodash';

const { TextArea } = Input;

interface User {
  _id: string;
  email: string;
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  externalSystemAppId?: string;
  plan?: {
    details?: {
      exclusiveUsers?: boolean;
    };
  };
}

interface IssueResolutionInterface {
  email: string;
  isAnExclusiveUser: boolean;
  issueTopic: string;
  resolutionMessage: string;
}

const ResolveTicket: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { post, get, loading } = useApi();
  
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const fetchUsers = async (searchTerm: string = '') => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token not found');
      }

      const apiBaseUrl = import.meta.env.VITE_NEXT_PUBLIC_GMIND_BASEURL || 'https://staging.golive.gmind.ai/v1/admin';
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      
      await get<any, any>(
        `/users?page=1&limit=50${searchParam}`,
        { Authorization: `Bearer ${token}` },
        {
          onSuccess: (response) => {
            if (response?.data?.users) {
              setUsers(response.data.users);
            } else if (Array.isArray(response?.data)) {
              setUsers(response.data);
            }
          },
          onError: (error) => {
            console.error('Error fetching users:', error);
            message.error('Failed to load users');
          }
        }
      );
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      fetchUsers(searchTerm);
    }, 500),
    []
  );

  const handleUserSelect = (value: string) => {
    const user = users.find(u => u._id === value);
    if (user) {
      setSelectedUser(user);
      const isExclusive = !!user.externalSystemAppId;
      const userEmail = user.email || user.emailAddress || '';
      form.setFieldsValue({ 
        userId: value,
        email: userEmail,
        isAnExclusiveUser: isExclusive 
      });
    }
  };

  const onFinish = async (values: any) => {
    const token = localStorage.getItem('token');
    
    const formData = new FormData();
    formData.append('email', values.email);
    formData.append('isAnExclusiveUser', String(values.isAnExclusiveUser || false));
    formData.append('issueTopic', values.issueTopic);
    formData.append('resolutionMessage', values.resolutionMessage);

    await post<any, any>(
      '/users/issue_resolution',
      formData,
      { Authorization: `Bearer ${token}` },
      {
        onSuccess: (response) => {
          message.success('Ticket resolved successfully!');
          form.resetFields();
          setSelectedUser(null);
          navigate('/home/all-users');
        },
        onError: (error) => {
          message.error('Failed to resolve ticket');
        }
      }
    );
  };

  const handleSearch = (value: string) => {
    if (value) {
      debouncedSearch(value);
    } else {
      fetchUsers('');
    }
  };

  const userOptions = users.map(user => ({
    value: user._id,
    label: `${user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim()} (${user.email || user.emailAddress})`,
  }));

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home/all-users')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Users</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Resolve Ticket</h1>
          <p className="text-gray-600 mt-2">Submit a resolution for a user's issue</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            {/* User Select */}
            <Form.Item
              name="userId"
              label={<span className="text-sm font-semibold text-gray-700">Select User</span>}
              rules={[{ required: true, message: 'Please select a user' }]}
            >
              <Select
                showSearch
                placeholder="Type to search users by name or email..."
                loading={loadingUsers}
                onSearch={handleSearch}
                onSelect={handleUserSelect}
                filterOption={false}
                options={userOptions}
                className="w-full"
                size="large"
                notFoundContent={loadingUsers ? 'Searching...' : 'No users found'}
              />
            </Form.Item>

            {/* Hidden email field */}
            <Form.Item name="email" hidden>
              <Input type="hidden" />
            </Form.Item>

            {/* Exclusive User Status (Read-only) */}
            {selectedUser && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">User Type:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedUser.externalSystemAppId 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {selectedUser.externalSystemAppId ? 'Exclusive User' : 'Regular User'}
                  </span>
                </div>
              </div>
            )}

            {/* Hidden field for isAnExclusiveUser */}
            <Form.Item name="isAnExclusiveUser" hidden>
              <Input type="hidden" />
            </Form.Item>

            {/* Issue Topic */}
            <Form.Item
              name="issueTopic"
              label={<span className="text-sm font-semibold text-gray-700">Issue Topic</span>}
              rules={[
                { required: true, message: 'Please enter the issue topic' },
                { min: 3, message: 'Topic must be at least 3 characters' }
              ]}
            >
              <Input
                placeholder="e.g., Payment Issue, Account Access, Feature Request"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            {/* Resolution Message */}
            <Form.Item
              name="resolutionMessage"
              label={<span className="text-sm font-semibold text-gray-700">Resolution Message</span>}
              rules={[
                { required: true, message: 'Please enter the resolution message' },
                { min: 10, message: 'Message must be at least 10 characters' }
              ]}
            >
              <TextArea
                rows={6}
                placeholder="Describe the resolution steps taken and any additional information..."
                className="rounded-lg"
              />
            </Form.Item>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end mt-8">
              <Button
                size="large"
                onClick={() => navigate('/home/all-users')}
                className="px-8"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                className="px-8 bg-[#E55109] hover:bg-[#d14808]"
                style={{ backgroundColor: '#E55109' }}
              >
                Submit Resolution
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ResolveTicket;
